import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDeleteUser } from "@api";

import { Modal } from "@ui/components";
import { Button, Notification, Text, Title } from "@ui/elements";
import { Stack } from "@ui/layout";

import { toaster, useAuth } from "@components";

type DeleteConfirmationModalProps = {
  onClose: () => void;
  open: boolean;
};

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  onClose,
  open,
}) => {
  const { t } = useTranslation();
  const {
    error: deleteAccountError,
    mutateAsync: deleteAccount,
    isPending,
  } = useDeleteUser();
  const authContext = useAuth();

  const handleUserDelete = async () => {
    await deleteAccount();

    await authContext.logout();

    toaster({
      title: t("messages.account-deleted"),
      message: t("messages.account-deleted-message"),
    });
  };

  return (
    <Modal
      isActive={open}
      onClose={onClose}
      header={t("messages.are-you-sure")}
      style={{ zIndex: 100 }}
    >
      <Stack direction="column" gap="xl">
        <Stack direction="column" gap="xl">
          <Title size={6}>{t("delete-account.confirmation")}</Title>
          <Stack direction="column">
            <Text>{t("delete-account.info-1")}</Text>
            <Text>{t("delete-account.info-2")}</Text>
          </Stack>
        </Stack>
        {deleteAccountError?.response?.data.error && (
          <Notification color="danger">
            <Text weight="bold">
              {deleteAccountError?.response?.data.error}
            </Text>
            <Text>{deleteAccountError?.response?.data.message}</Text>
          </Notification>
        )}
        {!deleteAccountError?.response?.data.error && deleteAccountError && (
          <Notification color="danger">
            <Text weight="bold">{String(deleteAccountError)}</Text>
            <Text>{deleteAccountError?.message}</Text>
          </Notification>
        )}
        <Stack justify="end">
          <Button variant="light" onClick={onClose}>
            {t("labels.cancel")}
          </Button>
          <Button
            variant="light"
            color="danger"
            onClick={handleUserDelete}
            isLoading={isPending}
          >
            {t("labels.im-sure")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export const DeleteAccount = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Stack direction="column" gap="xl">
      <DeleteConfirmationModal open={open} onClose={() => setOpen(false)} />
      <Text variant="secondary">{t("delete-account.description")}</Text>
      <Stack justify="end">
        <Button color="danger" variant="light" onClick={() => setOpen(true)}>
          {t("labels.delete-account")}
        </Button>
      </Stack>
    </Stack>
  );
};
